import React, { Suspense, useEffect } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";

import RouteFallback from "routes/RouteFallback";
import { clearPosthogSupportIdentity } from "config/posthog-runtime";
import { isPosthogEnabled } from "config";
// Config
import { ENVS, PATH_AUTH, PATH_DASHBOARD } from "config";
import { resolvePostLoginPath } from "utils/redirectUtils";

// Store
import { AppState } from "store/reducers/Reducer";
import { useSelector } from "react-redux";
import { useSessionUser } from "hooks/useSession";

export const PublicLayout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const loggedIn = useSelector((state: AppState) => state.auth.loggedIn);
  const { data: sessionUser, isLoading: isSessionLoading } = useSessionUser();

  useEffect(() => {
    if (loggedIn) {
      // Allow join route to proceed even when logged in
      if (location.pathname.startsWith("/join/")) {
        return;
      }
      // Allow email verification routes to proceed even when logged in
      if (
        location.pathname.startsWith(PATH_AUTH.verifyAccount) ||
        location.pathname.startsWith(PATH_AUTH.emailVerification)
      ) {
        return;
      }
      if (location.pathname === PATH_AUTH.resendEmail) {
        return;
      }

      if (isSessionLoading || !sessionUser) {
        return;
      }

      if (!sessionUser.verified && !ENVS.onPrem) {
        navigate(PATH_AUTH.resendEmail);
      } else {
        // A signed-in visit to /login?redirect=... goes straight to that page.
        navigate(resolvePostLoginPath(location.search, PATH_DASHBOARD.dashboard));
      }
    } else if (isPosthogEnabled) {
      clearPosthogSupportIdentity();
    }
  }, [navigate, loggedIn, sessionUser, isSessionLoading, location.pathname, location.search]);

  return (
    <Suspense fallback={<RouteFallback containerClassName="min-h-screen" />}>
      <Outlet />
    </Suspense>
  );
};
